import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';

/**
 * `event_venue` — the EXTRA venues of an event (linked `location` rows or
 * custom name+address rows). The event's own `location_id` is the primary
 * venue and is never a row here. DB-only; rules live in
 * `src/app/[locale]/event/[eventId]/libs/venues.ts`. Errors keep their
 * Postgres/PostgREST `code` so the loader can recognise a missing table.
 */
export type VenueLocationRow = {
  id: string;
  name: string;
  address: string | null;
  logo: string | null;
  website: string | null;
  phone: string | null;
  federation_id: string | null;
};
export type EventVenueRow = Database['public']['Tables']['event_venue']['Row'] & {
  location: VenueLocationRow | null;
};

export const VENUE_SELECT =
  'id, event_id, location_id, name, address, sort_order, photo_url, note, travel_min_to_next, created_at, updated_at, location:location_id ( id, name, address, logo, website, phone, federation_id )';

/** The organiser-entered columns (spec: Phase 4). `undefined` = leave alone, `null` = clear. */
export type VenueDetailsColumns = {
  photo_url?: string | null;
  note?: string | null;
  travel_min_to_next?: number | null;
};

export class EventVenueModelError extends Error {
  code: string | undefined;
  constructor(e: { message: string; code?: string }) {
    super(e.message);
    this.name = 'EventVenueModelError';
    this.code = e.code;
  }
}

type Client = SupabaseClient<Database>;
const fail = (e: { message: string; code?: string }) => new EventVenueModelError(e);

export const eventVenueModel = {
  async listByEventId(client: Client, eventId: string): Promise<EventVenueRow[]> {
    const { data, error } = await client
      .from('event_venue')
      .select(VENUE_SELECT)
      .eq('event_id', eventId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true })
      .order('id', { ascending: true });
    if (error) throw fail(error);
    return (data ?? []) as unknown as EventVenueRow[];
  },
  async listByEventIds(client: Client, eventIds: string[]): Promise<EventVenueRow[]> {
    if (eventIds.length === 0) return [];
    const { data, error } = await client
      .from('event_venue')
      .select(VENUE_SELECT)
      .in('event_id', eventIds)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true })
      .order('id', { ascending: true });
    if (error) throw fail(error);
    return (data ?? []) as unknown as EventVenueRow[];
  },
  async create(
    client: Client,
    input: {
      event_id: string;
      location_id?: string | null;
      name?: string | null;
      address?: string | null;
      sort_order: number;
    },
  ): Promise<EventVenueRow> {
    const { data, error } = await client
      .from('event_venue')
      .insert({
        event_id: input.event_id,
        location_id: input.location_id ?? null,
        name: input.name ?? null,
        address: input.address ?? null,
        sort_order: input.sort_order,
      })
      .select(VENUE_SELECT)
      .single();
    if (error) throw error; // raw: callers read error.code === '23505'
    return data as unknown as EventVenueRow;
  },
  async remove(client: Client, id: string, eventId: string): Promise<number> {
    const { error, count } = await client
      .from('event_venue')
      .delete({ count: 'exact' })
      .eq('id', id)
      .eq('event_id', eventId);
    if (error) throw fail(error);
    return count ?? 0;
  },
  async reorder(client: Client, eventId: string, ids: string[]): Promise<void> {
    for (let i = 0; i < ids.length; i++) {
      const { error } = await client
        .from('event_venue')
        .update({ sort_order: i })
        .eq('id', ids[i])
        .eq('event_id', eventId);
      if (error) throw fail(error);
    }
  },
  /** Update the organiser detail columns of one venue of one event; null when no row matched. */
  async update(
    client: Client,
    id: string,
    eventId: string,
    patch: VenueDetailsColumns,
  ): Promise<EventVenueRow | null> {
    const { data, error } = await client
      .from('event_venue')
      .update(patch)
      .eq('id', id)
      .eq('event_id', eventId)
      .select(VENUE_SELECT)
      .maybeSingle();
    if (error) throw fail(error);
    return (data as unknown as EventVenueRow | null) ?? null;
  },
  async findByEventAndLocation(
    client: Client,
    eventId: string,
    locationId: string,
  ): Promise<EventVenueRow | null> {
    const { data, error } = await client
      .from('event_venue')
      .select(VENUE_SELECT)
      .eq('event_id', eventId)
      .eq('location_id', locationId)
      .maybeSingle();
    if (error) throw fail(error);
    return (data as unknown as EventVenueRow | null) ?? null;
  },
};
